import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveCourse } from '../context/ActiveCourseContext';
import { questionAPI } from '../utils/api';
import Navbar from '../components/Navbar';
import {
  Tags, AlertCircle, BarChart2, ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';

// Ultra-Lightweight Fade-In Donut Chart with Glow
const AnimatedDonutChart = ({ tagAnalysis, onTagClick, totalQuestions }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Simple fade animation - 800ms
    let start = null;
    const duration = 800;

    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const newProgress = Math.min(elapsed / duration, 1);

      // Simple easing
      const eased = newProgress * newProgress * (3 - 2 * newProgress);
      setProgress(eased);

      if (newProgress < 1) {
        requestAnimationFrame(animate);
      } else {
        setAnimationComplete(true);
      }
    };

    requestAnimationFrame(animate);
  }, []);

  const colors = [
    { stroke: '#3B82F6', light: '#DBEAFE' },
    { stroke: '#A855F7', light: '#F3E8FF' },
    { stroke: '#10B981', light: '#D1FAE5' },
    { stroke: '#F59E0B', light: '#FEF3C7' },
    { stroke: '#EC4899', light: '#FCE7F3' },
    { stroke: '#6366F1', light: '#E0E7FF' },
    { stroke: '#EF4444', light: '#FEE2E2' },
    { stroke: '#14B8A6', light: '#CCFBF1' },
    { stroke: '#06B6D4', light: '#CFFAFE' },
    { stroke: '#F97316', light: '#FFEDD5' },
    { stroke: '#84CC16', light: '#ECFCCB' },
    { stroke: '#059669', light: '#D1FAE5' },
    { stroke: '#8B5CF6', light: '#EDE9FE' },
    { stroke: '#D946EF', light: '#FAE8FF' },
    { stroke: '#F43F5E', light: '#FFE4E6' },
  ];

  const total = tagAnalysis.reduce((sum, tag) => sum + tag.count, 0);
  let currentAngle = 0;

  const segments = tagAnalysis.map((tag, index) => {
    const percentage = (tag.count / total) * 100;
    const angle = (tag.count / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    return {
      ...tag,
      percentage,
      startAngle,
      endAngle,
      color: colors[index % colors.length]
    };
  });

  const createDonutSlice = (centerX, centerY, outerRadius, innerRadius, startAngle, endAngle) => {
    const startRad = (startAngle - 90) * Math.PI / 180;
    const endRad = (endAngle - 90) * Math.PI / 180;

    const x1 = centerX + outerRadius * Math.cos(startRad);
    const y1 = centerY + outerRadius * Math.sin(startRad);
    const x2 = centerX + outerRadius * Math.cos(endRad);
    const y2 = centerY + outerRadius * Math.sin(endRad);

    const x3 = centerX + innerRadius * Math.cos(endRad);
    const y3 = centerY + innerRadius * Math.sin(endRad);
    const x4 = centerX + innerRadius * Math.cos(startRad);
    const y4 = centerY + innerRadius * Math.sin(startRad);

    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  // Responsive sizing with 30% smaller hole
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const size = isMobile ? 320 : 420;
  const center = size / 2;
  const outerRadius = isMobile ? 140 : 180;
  const innerRadius = isMobile ? 63 : 80; // 30% smaller hole (was 90/115)

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl p-4 md:p-8 shadow-xl border border-gray-400 mb-10 overflow-hidden relative">
      {/* Minimal particles only after animation */}
      {animationComplete && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-blue-400 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${5 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>
      )}

      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 flex flex-col md:flex-row md:items-center gap-2 md:gap-3 relative z-10">
        <div className="flex items-center gap-3">
          <BarChart2 className="w-6 h-6 md:w-7 md:h-7 text-blue-600" />
          <span>Top Sub-Types Distribution</span>
        </div>
        <span className="text-xs md:text-sm font-normal text-gray-500 md:ml-auto">
          {tagAnalysis.length} categories
        </span>
      </h2>

      <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12 relative z-10">
        {/* Chart */}
        <div
          className="relative flex-shrink-0 mx-auto"
          style={{
            width: size,
            height: size,
            opacity: progress,
            transform: `scale(${0.95 + progress * 0.05})`,
            transition: 'opacity 0.3s ease, transform 0.3s ease'
          }}
        >
          <svg width={size} height={size} className="transform-gpu">
            <defs>
              {/* Simple gradients only */}
              {segments.map((segment, index) => (
                <linearGradient key={`grad-${index}`} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={segment.color.stroke} stopOpacity="0.9" />
                  <stop offset="100%" stopColor={segment.color.stroke} stopOpacity="1" />
                </linearGradient>
              ))}

              {/* Glow filter for hover */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Segments - simple fade in with glow on hover */}
            {segments.map((segment, index) => {
              const isHovered = hoveredIndex === index;
              const scale = isHovered ? 1.03 : 1;
              const currentOuter = outerRadius * scale;
              const currentInner = innerRadius * (isHovered ? 0.98 : 1);

              const path = createDonutSlice(center, center, currentOuter, currentInner, segment.startAngle, segment.endAngle);

              return (
                <g key={index}>
                  {/* Glow background layer on hover */}
                  {isHovered && (
                    <path
                      d={path}
                      fill={segment.color.stroke}
                      opacity="0.4"
                      style={{
                        filter: 'blur(8px)',
                      }}
                    />
                  )}

                  {/* Main segment */}
                  <path
                    d={path}
                    fill={`url(#gradient-${index})`}
                    stroke="white"
                    strokeWidth={isHovered ? 2.5 : 1.5}
                    className="cursor-pointer"
                    style={{
                      transition: 'all 0.2s ease-out',
                      opacity: isHovered ? 1 : 0.95,
                      transformOrigin: `${center}px ${center}px`,
                      filter: isHovered ? 'url(#glow)' : 'none',
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onClick={() => onTagClick(segment)}
                  />
                </g>
              );
            })}
          </svg>

          {/* Center Content - Only show on hover */}
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{
              opacity: hoveredIndex !== null && animationComplete ? 1 : 0,
              transition: 'opacity 0.2s ease-out'
            }}
          >
            {hoveredIndex !== null && (
              <div className="text-center px-4">
                <div
                  className="text-4xl md:text-6xl font-bold mb-2"
                  style={{
                    color: segments[hoveredIndex].color.stroke,
                    animation: 'slideUp 0.2s ease-out'
                  }}
                >
                  {segments[hoveredIndex].count}
                </div>
                <div
                  className="text-sm md:text-base font-bold mb-1"
                  style={{
                    color: segments[hoveredIndex].color.stroke
                  }}
                >
                  {segments[hoveredIndex]._id}
                </div>
                <div className="text-xs md:text-sm text-gray-600 font-semibold">
                  {segments[hoveredIndex].percentage.toFixed(1)}%
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legend - optimized */}
        <div className="flex-1 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 md:gap-2 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
            {segments.map((segment, index) => (
              <button
                key={index}
                onClick={() => onTagClick(segment)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className={`group relative flex items-center gap-1.5 md:gap-2 p-1.5 md:p-2 rounded-lg border-2 transition-all duration-200 ${hoveredIndex === index
                  ? 'border-blue-400 bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg scale-105'
                  : 'border-gray-400 bg-white hover:border-gray-400'
                  }`}
                style={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateX(0)' : 'translateX(20px)',
                  transition: `all 0.3s ease ${index * 0.03}s`
                }}
              >
                {/* Color indicator */}
                <div
                  className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: segment.color.stroke,
                    transform: hoveredIndex === index ? 'scale(1.2)' : 'scale(1)',
                    transition: 'transform 0.2s ease-out',
                    boxShadow: hoveredIndex === index ? `0 0 8px ${segment.color.stroke}40` : 'none'
                  }}
                />

                <div className="flex-1 text-left min-w-0">
                  <div
                    className="font-semibold text-gray-900 text-[11px] md:text-xs truncate transition-colors duration-200"
                    title={segment._id}
                    style={{
                      color: hoveredIndex === index ? segment.color.stroke : undefined
                    }}
                  >
                    {segment._id}
                  </div>
                  <div className="text-[10px] md:text-[11px] text-gray-500 font-medium">
                    {segment.count} • {segment.percentage.toFixed(1)}%
                  </div>
                </div>

                <ChevronRight
                  className="w-3 h-3 text-gray-400 transition-all duration-200"
                  style={{
                    transform: hoveredIndex === index ? 'translateX(2px)' : 'translateX(0)',
                    color: hoveredIndex === index ? segment.color.stroke : undefined
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(5px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .transform-gpu {
          transform: translateZ(0);
          will-change: transform;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3B82F6, #A855F7);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

const SubType = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { activeCourseId, activeCourseData } = useActiveCourse();
  const [tagAnalysis, setTagAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  useEffect(() => {
    setDemoMode(!isAuthenticated);

    if (activeCourseId) {
      fetchTags();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, activeCourseId]);

  const fetchTags = async () => {
    try {
      const response = await questionAPI.getQuestionsByTags(activeCourseId, {});
      setTagAnalysis(response.data.data);
    } catch (error) {
      console.error('Error fetching tag analysis:', error);
      console.error('Failed to load tag analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleTagClick = (tag) => {
    // Navigate to filtered page with the selected tag
    navigate('/filtered', {
      state: {
        filterType: 'tag',
        filterValue: tag._id,
        sourceType: 'subtype'
      }
    });
  };

  // Calculate statistics
  const totalQuestions = tagAnalysis.reduce((sum, tag) => sum + tag.count, 0);

  if (!activeCourseId) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-400">
            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Course</h2>
            <p className="text-gray-600 max-w-md mx-auto">Please activate a course from dashboard.</p>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                Sub Type Analysis
                {demoMode && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">
                    Demo Mode
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-2">
                Click on any sub-type to explore all questions in that category
              </p>
            </div>

            {activeCourseData && (
              <div className="bg-white px-5 py-3 rounded-xl shadow-sm border border-gray-400 flex items-center gap-3">
                <div>
                  <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Active Course</div>
                  <div className="font-bold text-gray-900">{activeCourseData.name}</div>
                </div>
              </div>
            )}
          </div>

          {/* Animated Donut Chart with Glow */}
          <AnimatedDonutChart
            tagAnalysis={tagAnalysis.slice(0, 15)}
            onTagClick={handleTagClick}
            totalQuestions={totalQuestions}
          />
        </div>

        {tagAnalysis.length === 0 && !loading && (
          <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-400">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Tags className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Tags Found</h3>
            <p className="text-gray-600 max-w-md mx-auto">No question tags available for this course yet.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SubType;
